/**
 * Field errors - the house pattern for showing an error UNDER a form element.
 *
 *   g.fieldError(target, message)  - show/update the message below the field
 *   g.fieldError(target, null)     - clear it
 *
 * `target` is the form element (or a selector). Where the message lands:
 *   - inside one of our component wrappers (.selekt / .dsel / .fsel): after
 *     the WHOLE component - below any file list, tag rows or grid - and the
 *     wrapper gets `genus-invalid` (each library styles its own control red);
 *   - a checkbox/radio inside .form-check: after the LAST .form-check of its
 *     name group (so group errors show under the whole group, never between
 *     a box and its label), and EVERY input of the group gets `is-invalid`;
 *   - anything else: right after the element, which gets `is-invalid`.
 * Multi-line messages are supported ('\n' separated).
 *
 * This is what the validation library calls; libraries also use it
 * themselves (e.g. file-selekt upload failures).
 */

// The checkbox/radio inputs sharing the target's name (the "group"; just the
// element itself when it is alone or nameless).
function checkGroup(el) {
	if (!el.name) return [el];
	const scope = el.form || document;
	const sel = 'input[type="' + el.type + '"][name="' + CSS.escape(el.name) + '"]';
	const group = Array.from(scope.querySelectorAll(sel));
	return group.length ? group : [el];
}

// Where the error box goes (and what nextElementSibling to check).
function errorRoot(el) {
	const wrap = el.closest('.selekt, .dsel, .fsel');
	if (wrap) return wrap;
	if ((el.type === 'checkbox' || el.type === 'radio') && el.closest('.form-check')) {
		const group = checkGroup(el);
		return group[group.length - 1].closest('.form-check') || el;
	}
	return el;
}

export function fieldError(target, message) {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	if (!el) return;
	const root = errorRoot(el);
	const isWrapper = root !== el && !root.classList.contains('form-check');
	const marked = isWrapper ? [] : (el.type === 'checkbox' || el.type === 'radio') ? checkGroup(el) : [el];

	let box = root.nextElementSibling;
	if (!(box && box.classList && box.classList.contains('genus-field-error'))) box = null;

	if (!message) {
		if (box) box.remove();
		if (isWrapper) root.classList.remove('genus-invalid');
		marked.forEach((input) => input.classList.remove('is-invalid'));
		return;
	}

	if (!box) {
		box = document.createElement('div');
		box.className = 'genus-field-error';
		root.parentNode.insertBefore(box, root.nextSibling);
	}
	box.textContent = message;
	if (isWrapper) root.classList.add('genus-invalid');
	marked.forEach((input) => input.classList.add('is-invalid'));
}
